import React from "react";
import { Button } from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getFirestore, collection, getDocs } from "firebase/firestore";

function ExportManager() {
  const db = getFirestore();

  // ===== Export-only ref helpers (no writes to Firestore) =====
  // Auto-fill missing ingredient.reference with a, b, c, ... then aa, ab, ...
  // Same ingredient name within a recipe gets the same letter.
  const withAutofilledRefs = (recipe) => {
    const cloned = JSON.parse(JSON.stringify(recipe || {}));

    const used = new Set();         // refs used in this recipe (lowercase)
    const nameMap = new Map();      // name(lowercase) -> ref
    let counter = 0;

    const nextLetter = () => {
      let num = counter++;
      let ref = "";
      do {
        ref = String.fromCharCode(97 + (num % 26)) + ref; // 97 = 'a'
        num = Math.floor(num / 26) - 1;
      } while (num >= 0);
      return ref; // a..z, aa..az, ba.. etc.
    };

    if (Array.isArray(cloned.servings)) {
      for (const s of cloned.servings) {
        const ings = Array.isArray(s?.ingredients) ? s.ingredients : [];
        for (const ing of ings) {
          const nameKey = (ing?.name || "").trim().toLowerCase();

          if (ing.reference && String(ing.reference).trim() !== "") {
            ing.reference = String(ing.reference).toLowerCase();
            used.add(ing.reference);
            if (nameKey && !nameMap.has(nameKey)) nameMap.set(nameKey, ing.reference);
          } else {
            if (nameKey && nameMap.has(nameKey)) {
              ing.reference = nameMap.get(nameKey);
            } else {
              let letter;
              do {
                letter = nextLetter();
              } while (used.has(letter));
              ing.reference = letter;
              used.add(letter);
              if (nameKey) nameMap.set(nameKey, letter);
            }
          }
        }
      }
    }

    return cloned;
  };

  const exportJSONPdf = async () => {
    const snapshot = await getDocs(collection(db, "drink_recipes"));
    const recipes = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Raw JSON Content - Teapioca Recipes (display lines)", 20, 20);

    let y = 30;

    for (const raw of recipes) {
      const recipe = withAutofilledRefs(raw);

      // Build a display-only JSON: ingredients -> string lines with [ref] first
      const printable = JSON.parse(JSON.stringify(recipe));
      if (Array.isArray(printable.servings)) {
        for (const serving of printable.servings) {
          if (Array.isArray(serving.ingredients)) {
            serving.ingredients = serving.ingredients.map((ing) => {
              const ref = ing.reference ? `[${ing.reference}] ` : "";
              const amount = (ing.amount ?? "") === "" ? "" : `${ing.amount} `;
              const unit = ing.unit ? `${ing.unit} ` : "";
              const name = ing.name || "";
              return `${ref}${amount}${unit}${name}`.trim();
            });
          }
        }
      }

      const jsonText = JSON.stringify(printable, null, 2);
      const lines = doc.splitTextToSize(jsonText, 170);

      if (y + lines.length * 4 > 280) {
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(10);
      doc.text(lines, 20, y);
      y += lines.length * 4 + 10;
    }

    doc.save("teapioca-recipes-json.pdf");
  };

  const exportPDF = async () => {
    const snapshot = await getDocs(collection(db, "drink_recipes"));
    const rawRecipes = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const recipes = rawRecipes.map(withAutofilledRefs);

    const doc = new jsPDF();

    // Flavors
    const flavorSnapshot = await getDocs(collection(db, "recipe_flavors"));
    const flavorMap = {};
    flavorSnapshot.docs.forEach((docRef) => {
      flavorMap[docRef.id] = docRef.data().name;
    });

    // Title
    doc.setFontSize(22);
    doc.text("Teapioca Recipe Book", 20, 30);
    doc.setFontSize(14);
    doc.text(`Exported: ${new Date().toLocaleString()}`, 20, 40);

    // Build categories
    const categories = {};
    for (const r of recipes) {
      if (r.active === false) continue;
      const cat = r.category || "Uncategorized";
      (categories[cat] ||= []).push(r);
    }

    // Table of Contents
    doc.addPage();
    doc.setFontSize(16);
    doc.text("Table of Contents", 20, 20);
    const tocData = [];
    for (const [category, list] of Object.entries(categories)) {
      for (const r of list) tocData.push([category, r.name]);
    }
    autoTable(doc, {
      startY: 30,
      head: [["Category", "Recipe"]],
      body: tocData,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [240, 240, 240] },
      margin: { left: 20, right: 20 },
    });

    // Recipe Notes
    doc.addPage();
    const notesSnapshot = await getDocs(collection(db, "recipe_notes"));
    const recipeNotes = notesSnapshot.docs
      .map((docRef) => ({ id: docRef.id, ...docRef.data() }))
      .filter((note) => !note.archived)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    doc.setFontSize(16);
    doc.text("Recipe Notes", 20, 20);
    let y = 30;
    for (const note of recipeNotes) {
      doc.setFontSize(14);
      doc.text(`• ${note.title}`, 20, y);
      y += 6;
      doc.setFontSize(8);
      doc.text(
        `Version: ${note.version || 1} | Last Updated: ${
          note.updated_at ? new Date(note.updated_at).toLocaleString() : "Unknown"
        }`,
        20,
        y
      );
      doc.setFontSize(10);
      y += 5;
      const lines = doc.splitTextToSize(note.content || "", 170);
      doc.text(lines, 20, y);
      y += lines.length * 4 + 3;

      doc.setDrawColor(200);
      doc.line(20, y, 190, y);
      y += 6;

      if (y > 260) {
        doc.addPage();
        y = 20;
      }
    }

    // Recipes
    doc.addPage();
    for (const category of Object.keys(categories)) {
      const list = categories[category];
      doc.setFontSize(20);
      doc.text(`${category}`, 20, 20);

      for (const recipe of list) {
        if (y > 260) {
          doc.addPage();
          y = 20;
        }

        doc.setFontSize(14);
        doc.text(`${recipe.name}`, 20, 30);
        doc.setFontSize(10);
        y = 36;

        if (recipe.alternative_names?.length) {
          doc.text(`Also known as: ${recipe.alternative_names.join(", ")}`, 20, y);
          y += 6;
        }

        if (recipe.supported_flavors?.length) {
          const flavorNames = recipe.supported_flavors.map((id) => flavorMap[id] || id);
          doc.text(`Supported Flavors: ${flavorNames.join(", ")}`, 20, y);
          y += 6;
        }

        if (Array.isArray(recipe.servings)) {
          for (const serving of recipe.servings) {
            doc.text(`${serving.size}:`, 20, y);
            y += 6;
            if (serving.ice_level) {
              doc.text(`Ice Level: ${serving.ice_level}`, 25, y);
              y += 6;
            }
            doc.text(`Ingredients:`, 25, y);
            y += 6;

            if (Array.isArray(serving.ingredients)) {
              for (const ing of serving.ingredients) {
                const ref = ing.reference ? `[${ing.reference}] ` : "";
                const amount = (ing.amount ?? "") === "" ? "" : `${ing.amount} `;
                const unit = ing.unit ? `${ing.unit} ` : "";
                const name = ing.name || "";
                doc.text(`- ${ref}${amount}${unit}${name}`.trim(), 25, y);
                y += 6;

                // Adjustment rules
                if (Array.isArray(ing.quantity_rules)) {
                  for (const rule of ing.quantity_rules) {
                    const count =
                      rule.addon_count !== undefined
                        ? `${rule.addon_count} Add-On${rule.addon_count > 1 ? "s" : ""}`
                        : "N/A";
                    const adjustmentLine = `-Adj for >${count}: ${rule.amount} ${ing.unit || ""}`.trim();
                    doc.setFontSize(10);
                    doc.text(adjustmentLine, 35, y);
                    y += 6;
                    doc.setFontSize(10);
                  }
                }
              }
            }
          }
        }

        if (recipe.preparation_method?.container) {
          doc.text(`Prep Container: ${recipe.preparation_method.container}`, 20, y);
          y += 6;
        }

        if (recipe.preparation_method?.steps?.length) {
          doc.text(`Steps:`, 20, y);
          y += 6;
          for (let i = 0; i < recipe.preparation_method.steps.length; i++) {
            const step = recipe.preparation_method.steps[i];
            doc.text(`${i + 1}. ${step}`, 25, y);
            y += 6;
          }
        }

        if (recipe.notes?.length) {
          doc.text(`Notes:`, 20, y);
          y += 6;
          for (let i = 0; i < recipe.notes.length; i++) {
            const note = recipe.notes[i];
            doc.text(`${i + 1}. ${note}`, 25, y);
            y += 6;
          }
          y += 4;
        }

        doc.setFontSize(7);
        doc.text(
          `Version: ${recipe.version || 1} | Last Updated: ${recipe.updated_at || "Unknown"}`,
          20,
          y
        );
        y += 5;
        doc.setFontSize(10);

        const isLastRecipe =
          category === Object.keys(categories).at(-1) &&
          recipe === list.at(-1);

        if (!isLastRecipe) {
          doc.addPage();
          y = 20;
        }
      }
    }

    doc.save("teapioca-recipe-book.pdf");
  };

  return (
    <>
      <Button onClick={exportPDF} variant="outlined" style={{ marginRight: 10 }}>
        Export Recipe (PDF)
      </Button>
      <Button onClick={exportJSONPdf} variant="outlined" color="secondary">
        Export Recipe (JSON PDF)
      </Button>
    </>
  );
}

export default ExportManager;
